import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Styled Components
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const LegendWrap = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  font-size: 12px;
  color: #444;
  margin-bottom: 6px;
`;
const Swatch = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${(p) => p.color || "#ccc"};
  margin-right: 6px;
`;

// ---- Colors
const GREEN  = "#2e7d32";
const ORANGE = "#fb8c00";

const TransactionsByRegionChart = ({ salesData = [], clientsData = [] }) => {
  const [regionTransactionData, setRegionTransactionData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);

  // Region map (client -> pretty)
  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie", ŚLĄSKIE: "śląskie", PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie", ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie", DOLNOŚLĄSKIE: "dolnośląskie", ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie", POMORSKIE: "pomorskie", LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie", PODLASKIE: "podlaskie",
    "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
    MAŁOPOLSKIE: "małopolskie",
  };

  // Defaults (last year, whole year)
  useEffect(() => {
    if (!salesData.length || !clientsData.length) return;
    const availableYears = [...new Set(salesData.map(r => parseInt(r.Rok)))].sort((a, b) => b - a);
    const defaultYear = availableYears[0];
    setSelectedYear(defaultYear);
    setSelectedMonth(0); // "Cały rok"
  }, [salesData, clientsData]);

  // Build dataset (current vs same period prev year)
  useEffect(() => {
    if (!selectedYear) return;

    const kontrahentaToRegion = {};
    clientsData.forEach((c) => {
      const nip = c.nip?.trim();
      const region = c.wojewodztwo?.trim()?.toUpperCase();
      if (nip) kontrahentaToRegion[nip] = regionNameMapping[region] || "Unknown";
    });

    const countByRegion = (year, monthOrZero) => {
      const filtered = salesData.filter(r => {
        const rok = parseInt(r.Rok);
        const mies = parseInt(r.Miesiac);
        return rok === year && (monthOrZero === 0 || mies === monthOrZero);
      });

      const counts = {};
      filtered.forEach(row => {
        const nip = row.NrKontrahenta?.trim();
        const region = kontrahentaToRegion[nip] || "Unknown";
        if (region === "Unknown") return;
        counts[region] = (counts[region] || 0) + 1;
      });
      return counts;
    };

    const currentCounts = countByRegion(selectedYear, selectedMonth);
    const prevYearCounts = countByRegion(selectedYear - 1, selectedMonth);

    const regionsSet = new Set([...Object.keys(currentCounts), ...Object.keys(prevYearCounts)]);

    const rows = [...regionsSet].map(region => {
      const current = currentCounts[region] || 0;
      const prev = prevYearCounts[region] || 0;
      const diff = current - prev;
      const pct = prev === 0 ? null : (diff / prev) * 100;
      return { region, current, prev, diff, pct };
    });

    rows.sort((a, b) => b.current - a.current);
    setRegionTransactionData(rows);
  }, [salesData, clientsData, selectedYear, selectedMonth]);

  const getMonthName = (m) => new Date(2000, m - 1).toLocaleString("pl-PL", { month: "long" });
  const availableYears = [...new Set(salesData.map(r => parseInt(r.Rok)))].sort((a, b) => b - a);
  const availableMonths = [...new Set(
    salesData.filter(r => parseInt(r.Rok) === selectedYear).map(r => parseInt(r.Miesiac))
  )].sort((a, b) => a - b);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const p = Object.fromEntries(payload.map(it => [it.dataKey, it.value]));
    const diff = (p.current ?? 0) - (p.prev ?? 0);
    const pct = (p.prev ?? 0) === 0 ? null : (diff / p.prev) * 100;
    return (
      <div style={{ background: "#fff", border: "1px solid #ddd", padding: 10, fontSize: 12 }}>
        <div style={{ fontWeight: "bold", marginBottom: 6 }}>{label}</div>
        <div>Obecny okres: <strong>{(p.current ?? 0).toLocaleString()}</strong></div>
        <div>Rok wcześniej: <strong>{(p.prev ?? 0).toLocaleString()}</strong></div>
        <div>Różnica: <strong>{diff.toLocaleString()}</strong></div>
        <div>% zmiany: <strong>{pct === null ? "—" : `${pct.toFixed(2)}%`}</strong></div>
      </div>
    );
  };

  // Custom legend to reflect our palette (no red note)
  const LegendWrap = styled.div`
    display: flex;
    gap: 18px;
    align-items: center;
    font-size: 12px;
    color: #444;
    margin-bottom: 6px;
  `;
  const Swatch = styled.span`
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${(p) => p.color || "#ccc"};
    margin-right: 6px;
  `;
  const CustomLegend = () => (
    <LegendWrap>
      <span><Swatch color={GREEN} />Obecny okres</span>
      <span><Swatch color={ORANGE} />Rok wcześniej</span>
    </LegendWrap>
  );

  return (
    <div style={{ margin: "20px auto", width: "90%" }}>
      <ChartTitle>
        Liczba transakcji wg województw (z porównaniem r/r)
        <InfoIconWrapper data-tooltip-id="transactions-region-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="transactions-region-tooltip"
        place="left"
        content={
          <>
            <strong>Liczba transakcji</strong>
            <br />
            Wykres pokazuje liczbę transakcji w podziale na województwa
            dla wybranego okresu oraz dla tego samego okresu rok wcześniej.
          </>
        }
      />

      <Controls>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
            <option value={0}>Cały rok</option>
            {availableMonths.map(m => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </Select>
        </div>
      </Controls>

      {/* Custom legend that matches colors */}
      <CustomLegend />

      <ResponsiveContainer width="100%" height={520}>
        <BarChart data={regionTransactionData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" tickFormatter={(val) => val.toLocaleString()} />
          <YAxis type="category" dataKey="region" width={150} />
          <Tooltip content={<CustomTooltip />} />

          {/* Current = GREEN (uniform) */}
          <Bar dataKey="current" name="Obecny okres" barSize={26} fill={GREEN} />

          {/* Previous = ORANGE */}
          <Bar dataKey="prev" name="Rok wcześniej (ten sam okres)" barSize={26} fill={ORANGE} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TransactionsByRegionChart;
